import AutocompleteField from "../common/AutocompleteField";
import { getCountryById, getCountryDataWithoutExcludedCountries } from "../../util/sifarnikUtil";
import InputField from "../common/InputField";
import React from "react";
import { TaxResidencyCountryInformation } from "../../model/TaxResidencyType";
import { Box, FormControl, Grid, InputLabel, Tooltip } from "@mui/material";
import { FormikErrors } from "formik";
import { useMediaQuery } from "react-responsive";
import deleteButton from "../../images/icon-delete.svg";
import disabledDeleteButton from "../../images/disabled-icon-delete.svg";
import { useTranslation } from "react-i18next";
import { SupportedLanguage } from "../../i18n";

type TaxResidencyCountryInputProps = {
    index: number;
    handleValueChange: (value: any, field?: string) => void;
    handleValidation: (value: any, field?: string) => boolean;
    handleRemoval: (index: number) => void;
    isRemovalDisabled?: boolean;
    excludedCountries?: string[];
    className?: string;
    error?: FormikErrors<TaxResidencyCountryInformation>;
    value?: TaxResidencyCountryInformation;
};

export const TaxResidencyCountryInput = ({
                                             index,
                                             handleValueChange,
                                             handleValidation,
                                             handleRemoval,
                                             isRemovalDisabled,
                                             excludedCountries,
                                             error,
                                             value,
                                         }: TaxResidencyCountryInputProps) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const isMobile = useMediaQuery({ maxWidth: 767 });
    const isVerySmallMobile = useMediaQuery({ maxWidth: 350 });

    return (
      <FormControl sx={{ alignContent: 'center' }}>
          <InputLabel sx={{ backgroundColor: 'white', paddingInline: '6px' }} shrink>
              {t("tax-residency-page.country-information.info")}
          </InputLabel>
          <Box sx={{
              padding: '24px',
              borderWidth: '1px',
              borderRadius: '24px',
              borderColor: '#9C9FA3',
              backgroundColor: '#f9fafb',
          }}>
              <Grid container spacing={2}>
                  <Grid size={isMobile ? 12 : 5}>
                      <Grid container spacing={2}>
                          <Grid size={isMobile && !isVerySmallMobile ? 9 : 12}>
                              <AutocompleteField
                                name={`drzaveOporezivanja[${index}].country`}
                                label={t("tax-residency-page.country-information.country-field.label")}
                                options={getCountryDataWithoutExcludedCountries(excludedCountries ?? [], lng)}
                                getOptionLabel={(option) => option?.drzava ? option.drzava : ""}
                                isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                defaultValue={value?.country ?
                                  {
                                    oznaka: value?.country.oznaka,
                                    drzava: getCountryById(value?.country.oznaka, lng).drzava
                                  }
                                  : undefined
                                }
                                error={t(error?.country as string)}
                                handleValueChange={handleValueChange}
                                handleValidation={handleValidation}
                              />
                          </Grid>
                          {isMobile && !isVerySmallMobile &&
                            <TaxResidencyCountryInputRemoveButton
                              onClick={() => handleRemoval(index)}
                              isDisabled={isRemovalDisabled}
                            />
                          }
                      </Grid>
                  </Grid>
                  <Grid size={isMobile ? 12 : 7}>
                      <Grid container spacing={2}>
                          <Grid size={isMobile ? 12 : 9}>
                              {value?.country?.oznaka && value.country.oznaka !== "HR" && (
                                <InputField
                                  className="md:ml-4"
                                  name={`drzaveOporezivanja[${index}].taxNumber`}
                                  label={t("tax-residency-page.country-information.tax-number-field.label")}
                                  info={t("tax-residency-page.country-information.tax-number-field.info")}
                                  maxLength={30}
                                  inputValue={value?.taxNumber}
                                  error={t(error?.taxNumber as string)}
                                  handleValueChange={handleValueChange}
                                  handleValidation={handleValidation}
                                />
                              )}
                          </Grid>
                          {!isMobile && (
                            <TaxResidencyCountryInputRemoveButton
                              onClick={() => handleRemoval(index)}
                              isDisabled={isRemovalDisabled}
                            />
                          )}
                      </Grid>
                  </Grid>
                  {isVerySmallMobile &&
                    <TaxResidencyCountryInputRemoveButton
                      onClick={() => handleRemoval(index)}
                      isDisabled={isRemovalDisabled}
                    />
                  }
              </Grid>
          </Box>
      </FormControl>
    );
}

type TaxResidencyCountryInputRemoveButtonProps = {
    onClick: () => void;
    isDisabled?: boolean;
}

const TaxResidencyCountryInputRemoveButton = ({ onClick, isDisabled }: TaxResidencyCountryInputRemoveButtonProps) => {
    return (
      <div className="mt-3 mx-auto my-auto">
          <Tooltip
            slotProps={{
                tooltip: {
                    sx: {
                        fontSize: '14px',
                    },
                },
            }}
            placement="top"
            title={isDisabled
              ? undefined
              : <span className={"font-base"} style={{ whiteSpace: 'pre-line' }}>Ukloni državu</span>}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            arrow
          >
              <div>
                  <button
                    type="button"
                    onClick={onClick}
                    disabled={isDisabled}
                    className="text-red-500 hover:text-red-700 shrink-0"
                  >
                      <img
                        className="w-8 h-8"
                        src={isDisabled ? disabledDeleteButton : deleteButton}
                        alt="Delete icon"
                      />
                  </button>
              </div>
          </Tooltip>
      </div>
    )
}
